import { Paths, PathValue } from "@mctypes/global.types";
import { useEffect } from "react";
import { Button, Paper, Stack, Title } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../components";
import {
  IGKRapport,
  IGKTrykkfallIAvtrekksskapResultater,
} from "../../../../../../mc-shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../ReportBoard/ReportDocxImageContainer";
import { beregnetTverrsnittareal } from "../gkGlobalUtils";
const TrykkfallIAvtrekksskap: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKTrykkfallIAvtrekksskapResultater;
}> = ({ data, onUpdateEditData }) => {
  useEffect(() => {
    utdataBeregninger();
  }, [data.inndata.diameterPaaAvtrekkskanal]);
  const addNewRow = () => {
    const maaleresultaterArray = data.inndata.maaleresultaterMaalenipler;
    maaleresultaterArray.push(null);
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler", maaleresultaterArray);
    return maaleresultaterArray;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = data.inndata.maaleresultaterMaalenipler;
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData(
        "testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler",
        updatedMaaleresultater,
      );
    }
  };
  const updateMaaleresultater = (resultat, i) => {
    const maaleresultater = [...data.inndata.maaleresultaterMaalenipler];
    maaleresultater[i] = resultat;
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler", maaleresultater);
  };
  const utdataBeregninger = () => {
    const tverrsnittareal = beregnetTverrsnittareal(data.inndata.diameterPaaAvtrekkskanal);
    const utdata = {
      tverrsnittareal: tverrsnittareal,
    };
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.utdata", utdata);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>Trykkfall i avtrekksskap</Title>
        <h5>Avtrekkskanal:</h5>
        <div className="d-flex flex-column mt-0 mb-4">
          <McInputNew
            label="Antall målepunkter på avtrekkskanal (Minimum 4):"
            value={data.inndata.antallMaalepunkter}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.antallMaalepunkter",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            className="w-25"
          />
        </div>
        <div className="d-flex flex-column mt-2">
          <b>Måletiden er 60 sekunder &#40;s&#41;:</b>
        </div>
        <div className="d-flex flex-column mt-2 w-50">
          <McTextAreaNew
            label="Beskrivelse av målepunkter:"
            value={data.inndata.beskrivelseAvMaalepunkter}
            onChange={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.beskrivelseAvMaalepunkter",
                e.target.value,
              )
            }
          />
        </div>
        <div className="mt-4 d-flex flex-column">
          <McInputNew
            label="Diameter på avtrekkskanal hvor målinger ble utført (m):"
            value={data.inndata.diameterPaaAvtrekkskanal}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.diameterPaaAvtrekkskanal",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            className="w-25"
          />
        </div>
        <div className="d-flex justify-content-between">
          <p className="mb-0">
            Tversnittsareal kanal &#40;m<sup>2</sup>&#41; ={" "}
            <b>{beregnetTverrsnittareal(data.inndata.diameterPaaAvtrekkskanal)?.toFixed(3) ?? ""}</b>
          </p>
          <p className="mb-0">
            Formel: &#40;Ak = PI * &#40;D / 2&#41;<sup>2</sup>&#41;
          </p>
        </div>
        <hr className="mt-0" />
        <div className="mt-4 d-flex flex-column">
          <McInputNew
            label="Måleresultat tilkoblingspunkt på avtrekkskanal (Pa):"
            value={data.inndata.maaleresultatTilkoblingspunkt}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.maaleresultatTilkoblingspunkt",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            className="w-25"
          />
        </div>
        <div className="mt-4">
          <h5>Målenippel:</h5>
          <b>Legg til innebygd målenippel:</b>
          <div className="d-flex">
            <Button color="green" onClick={addNewRow}>
              Legg til rad
            </Button>
            <HSpace />
            <Button variant="danger-filled" onClick={removeLastRow}>
              Fjern siste rad
            </Button>
          </div>
        </div>
        {data.inndata.maaleresultaterMaalenipler.length > 0 && (
          <div className="mt-4 d-flex flex-column">
            <b>Fyll inn måleresultater &#40;Pa&#41;:</b>
            {data.inndata.maaleresultaterMaalenipler.map((a, i) => (
              <McInputNew
                className="mb-1 w-25"
                key={i}
                value={a}
                normalizeNumbers={true}
                type="text"
                onBlur={(e) => updateMaaleresultater(Number(e.target.value), i)}
              />
            ))}
          </div>
        )}
        <div className="mt-4 d-flex flex-column mb-4">
          <McInputNew
            label={<>
              Avtrekksluftmengde &#40;m<sup>3</sup>/s&#41;:
            </>}
            value={data.inndata.avtrekksLuftmengde}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.avtrekksLuftmengde",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            className="w-25"
          />
        </div>
        <div className="mt-2">
          <ReportDocxImageContainer docxImageTag="trykkfallIAvtrekksskap" title="Målepunkter" />
          <div className="w-50">
            <McTextAreaNew
              label="Kommentar:"
              value={data.inndata.kommentar}
              onChange={(e) =>
                onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.kommentar", e.target.value)
              }
            />
          </div>
        </div>
      </Stack>
    </Paper>
  );
};
export default TrykkfallIAvtrekksskap;